import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '@/lib/LanguageContext';

export default function HeroSection({ heroImage, eyebrow, lead, accent, text }) {
  const { t, language } = useLang();
  const containerRef = useRef(null);

  useEffect(() => {
    // Skip parallax on touch/small screens (mobile/tablet) — no mousemove there anyway,
    // and it avoids attaching an unnecessary listener plus main-thread work.
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const imgEl = containerRef.current?.querySelector('.hero-parallax');
    if (!imgEl) return;

    let rafId = null;
    let lastX = 0;
    let lastY = 0;

    const applyTransform = () => {
      imgEl.style.transform = `translate(${lastX}px, ${lastY}px) scale(1.1)`;
      rafId = null;
    };

    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      lastX = (clientX / innerWidth - 0.5) * 20;
      lastY = (clientY / innerHeight - 0.5) * 20;
      if (rafId === null) rafId = requestAnimationFrame(applyTransform);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section id="hero" ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt=""
          fetchPriority="high"
          decoding="async"
          className="hero-parallax w-full h-full object-cover opacity-40 scale-110"
          style={{ willChange: 'transform' }} />
        
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-background/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-32 pb-24 md:pt-36 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="font-inter font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[1.05] mb-6">
          
          <span className="text-foreground">{(lead || t('Custom AI, Software & Cybersecurity')).replace(/\.?$/, '.')}</span>
          <br />
          <span className="inline-block pb-1 uppercase text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-primary">{String((lead ? accent : t('— Built in Dubai')) || '').replace(/^\s*[—–-]\s*/, '')}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="text-muted-foreground max-w-2xl mx-auto leading-relaxed font-inter font-light mb-8 text-base md:text-base text-center">
          
          {text || t('Jotunheims is a Dubai-based technology company and venture studio building AI, software, cybersecurity and next-generation ventures for ambitious businesses and founders worldwide.')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4">
          
          <Link
            to="/contact"
            className="group px-8 py-4 bg-primary text-primary-foreground font-inter font-semibold text-sm tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20">
            
            {t('Get a Quote')}
          </Link>
          <Link
            to="/prices"
            className="px-8 py-4 border border-border text-foreground font-inter font-medium text-sm tracking-wider uppercase rounded-lg hover:border-primary/50 hover:text-primary transition-all duration-300">
            
            {t('View Prices')}
          </Link>
        </motion.div>
        <Link to="/partners" className="inline-block mt-5 text-xs font-inter tracking-[0.2em] uppercase text-muted-foreground hover:text-primary transition-colors">
          {t('Explore a Partnership')} →
        </Link>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="text-xs font-inter font-medium tracking-[0.3em] uppercase text-primary mt-6">
          {eyebrow || t('Powering Innovation, Empowering You')}
        </motion.p>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2">
        
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2">
          
          <ChevronDown className="w-4 h-4 text-primary/60" />
        </motion.div>
      </motion.div>
    </section>);

}